import React from 'react';
import { Container, Typography, Grid, Box, Paper } from '@mui/material';
import { motion } from 'framer-motion';

const features = [
  {
    title: 'Automated Billing',
    description: 'Save time with seamless, recurring billing for customers.',
  },
  {
    title: 'Integrated Payments',
    description: 'Accept payments via MPesa, credit cards, and more.',
  },
  {
    title: 'Hotspot Billing',
    description: 'Manage user logins, track usage, and automate billing for hotspot networks.',
  },
  {
    title: 'PPPoE Billing',
    description: 'Offer secure PPPoE connections and automate billing for these services.',
  },
  {
    title: 'Static IP Management',
    description: 'Handle static IP assignments with ease and integrate it into your billing system.',
  },
  {
    title: 'Customer Management',
    description: 'Easily track and manage customer data, balances, and more.',
  },
  {
    title: 'SMS Alerts',
    description: 'Notify customers of overdue payments or plan renewals.',
  },
  {
    title: 'MikroTik Remote Management',
    description: 'Securely manage MikroTik routers remotely, perform backups, and apply configurations in real-time.',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: i * 0.08,
      ease: [0.25, 0.1, 0.25, 1],
    },
  }),
};

const Features = () => {
  return (
    <Box
      component="section"
      id="features"
      sx={{
        py: { xs: 10, md: 18 },
        backgroundColor: '#ffffff',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.03em',
              background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
            }}
          >
            Why Choose NasRadius?
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: 'rgba(29,29,31,0.65)',
              fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
              maxWidth: '600px',
              mx: 'auto',
              mb: { xs: 6, md: 10 },
              lineHeight: 1.6,
            }}
          >
            Everything you need to run your ISP billing operations, beautifully crafted in one platform.
          </Typography>
        </motion.div>

        <Grid container spacing={{ xs: 3, md: 4 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                custom={index}
                style={{ height: '100%' }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.06)',
                    backgroundColor: '#fafafa',
                    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                      backgroundColor: '#f0f7ff',
                      borderColor: 'rgba(11,116,193,0.15)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: '#1d1d1f',
                      letterSpacing: '-0.02em',
                      mb: 1.5,
                      fontSize: { xs: '1.15rem', md: '1.4rem' },
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(29,29,31,0.65)',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      lineHeight: 1.6,
                      maxWidth: '280px',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Features;
